
import React from 'react';
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { EntryDialogContent } from "./dialog-sections/DialogContent";
import { DeleteConfirmationDialog } from "./dialog-sections/DeleteConfirmationDialog";
import { KnowledgeEntry } from "./types";
import { useKnowledgeBase } from "./useKnowledgeBase";

interface KnowledgeEntryDialogProps {
  entry: KnowledgeEntry;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate?: () => Promise<void> | void;  // Make onUpdate optional
}

export function KnowledgeEntryDialog({ entry, isOpen, onOpenChange, onUpdate }: KnowledgeEntryDialogProps) {
  const { handleDelete, isDeleting } = useKnowledgeBase();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = React.useState(false);

  // Get the AI-generated title or fall back to the original title
  const displayTitle = entry.metadata?.generated_title || entry.title || "Untitled Entry";

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await handleDelete(entry);
      setShowDeleteConfirmation(false);
      onOpenChange(false);
      if (onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Failed to delete entry:', error);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>{displayTitle}</DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            <EntryDialogContent entry={entry} />
          </div>
          
          <DialogFooter className="mt-4 flex justify-between">
            <Button 
              variant="destructive" 
              onClick={handleDeleteClick}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
            <DialogClose asChild>
              <Button type="button" variant="secondary">Close</Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      <DeleteConfirmationDialog
        isOpen={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        title="Delete Knowledge Entry"
        description={`Are you sure you want to delete "${displayTitle}"? This action cannot be undone.`}
      />
    </>
  );
}
